import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";

import { TrialHighlight } from "../components/TrialHighlight";
import {
  TIER_BANDS,
  TIER_SOURCE,
  ANNUAL_IS_BILLABLE,
  INCLUDED_EVERYWHERE,
  MODULES,
  TIER_MINIMUM_MONTHLY,
  BILL_CEILING_MULTIPLE,
  formatInr,
  formatInrPaise,
  bandRange,
  billCeiling,
} from "../domain/tiers";
import {
  CLOUD_PLANS,
  CLOUD_SOURCE,
  CLOUD_GST_PERCENT,
  CLOUD_OVERAGE,
  CLOUD_EXTRAS,
  CLOUD_INCLUDED,
  CLOUD_BILL_CEILING_MULTIPLE,
  CU_HOURS_ALWAYS_ON,
  formatGb,
  invoiceTotal,
} from "../domain/cloudPlans";
import { VersionLine } from "../components/VersionLine";
import { gstNotRegisteredNotice, gstRegisteredNotice } from "../domain/entity";

/**
 * 🔑 The prices come from `domain/tiers`, which already carries the band table,
 * the Indian-numbering currency formatter and 22 tests. This page renders that
 * module and defines no numbers of its own -- a second copy of the price table
 * on the marketing page is exactly how the page and the invoice come to
 * disagree, and the invoice is the one that would be right.
 */
const PAID_BANDS = TIER_BANDS.filter((b) => !b.negotiated);
const ENTERPRISE = TIER_BANDS.find((b) => b.negotiated);

export function Pricing() {
  const auth = useAuth();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <Button size="small" onClick={() => void auth.signinRedirect()}>
            Sign in
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: 30, md: 40 } }}>
          Pricing
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 1 }}>
          One monthly subscription per operator, priced by how many active subscribers you carry.
        </Typography>

        {/* ⛔ DELIBERATELY NOT A PLAN PICKER, and this is not an omission to
            fix later. The tier is DERIVED from your measured active subscriber
            count -- it is never self-declared. A dropdown here would be the
            obvious design, would pass review, and would be wrong: it would let
            an operator select a band cheaper than the one their subscriber
            count puts them in, and the invoice would then contradict the
            screen that sold it. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You do not pick a tier. Your band follows your active subscriber count, so you move up
          and down automatically as your network grows or shrinks. Hosting your own application
          instead? See <a href="#cloud">Cloud hosting</a> below.
        </Typography>

        {/* One component, three pages. See components/TrialHighlight. */}
        <Box sx={{ mb: 4 }}>
          <TrialHighlight />
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {PAID_BANDS.map((b) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={b.tier}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {b.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {formatInr(b.price ?? 0)}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {" "}
                      /month
                    </Typography>
                  </Typography>
                  {/* 🔑 The annual rate is shown but NEVER as an equal option.
                      radbilling has one billing period and it is monthly, so
                      presenting two selectable prices would offer something the
                      platform cannot invoice. The monthly figure is the price;
                      annual is an arrangement. */}
                  {b.priceAnnual !== null && (
                    <Typography variant="body2" color="text.secondary">
                      {formatInr(b.priceAnnual)}/month paid annually —{" "}
                      {ANNUAL_IS_BILLABLE ? "available now" : "by arrangement, ask us"}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{bandRange(b)}</strong> active subscribers
                  </Typography>
                  {b.overagePerSubscriber !== null && (
                    <Typography variant="body2" color="text.secondary">
                      {formatInrPaise(b.overagePerSubscriber)} per extra subscriber
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="baseline">
              <Typography variant="overline" color="text.secondary">
                Enterprise
              </Typography>
              <Chip size="small" label={`${ENTERPRISE ? bandRange(ENTERPRISE) : ""} subscribers`} />
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Negotiated per operator — no published price. Everything in the list below is
              included, as it is on every plan.
            </Typography>
          </CardContent>
        </Card>

        {/* 🔑 One block, not a row of five identical ticks across the plan
            cards. A tick in every column reads as a feature comparison; the
            point here is that there is nothing to compare. */}
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Included on every plan, including Enterprise
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
              {INCLUDED_EVERYWHERE.map((item) => (
                <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                  {item}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Sub-operators are free because a node costs us nothing — what costs is subscribers,
              and <strong>your sub-operators&rsquo; subscribers count toward your own total</strong>.
              Charging per node would only make you flatten your hierarchy.
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          The floor, and the ceiling
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3, color: "text.secondary" }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            <strong>Minimum {formatInr(TIER_MINIMUM_MONTHLY)} a month.</strong> A floor, not a
            rate — below the Launch allowance you pay the Launch price, not less.
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Go past your allowance and the first 10% is free, once a quarter. Past that you pay the
            per-subscriber rate on your card, and after two months over we move you up a plan —
            because <strong>upgrading is always cheaper than paying overage</strong>, and we would
            rather you were on the right plan than on a surprising bill.
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            <strong>Your bill is capped at {BILL_CEILING_MULTIPLE}× your plan price</strong> —{" "}
            {PAID_BANDS[0] ? formatInr(billCeiling(PAID_BANDS[0]) ?? 0) : ""} on Launch. You can
            raise your own cap from the console, so it protects you without ever blocking growth.
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            {/* Non-negotiable and said on the price page, not buried in the
                Terms: it is the objection that stops an operator moving their
                whole subscriber base onto someone else's system. */}
            <strong>We never delete or withhold your data for a commercial reason.</strong> Past
            the cap we block new writes and phone you. What is stored stays readable and
            exportable, always.
          </Typography>
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Modules
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Priced by capability, never per entity — you pay once for what a module does, not per
          sub-operator, per seat or per device you point it at.
        </Typography>
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {MODULES.map((m) => (
            <Grid size={{ xs: 12, sm: 6 }} key={m.key}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {m.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                    {m.blurb}
                  </Typography>
                  {/* ⛔ A module whose price is a SHAPE rather than a number
                      renders the shape. A dash in a price column reads as
                      "free", and a number we do not have reads as quotable. */}
                  {m.priceEntry === null ? (
                    <Chip size="small" variant="outlined" label={m.basis ?? "Ask us"} />
                  ) : (
                    <Typography variant="body2">
                      From <strong>{formatInr(m.priceEntry)}</strong>/month, by plan size
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          What counts, and what does not
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3, color: "text.secondary" }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            An <strong>active subscriber</strong> is the billing metric, and it includes your free
            and trial subscribers.
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            <strong>Operator staff logins are unlimited and never charged for.</strong> Metering
            them would only make your team share one login, which is exactly what your audit trail
            cannot afford.
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Billing is <strong>monthly in arrears</strong> — you are invoiced for the month just
            served, not the month ahead.
          </Typography>
          <Typography component="li" variant="body2">
            Prices are per operator. Sub-operators beneath you roll up into your own count.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* The second product. 🔑 Unlike the operator bands above these ARE
            chosen by the tenant, so presenting them side by side as options is
            correct here -- see domain/cloudPlans. */}
        <Box id="cloud" sx={{ scrollMarginTop: 16 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: 26, md: 32 } }}>
            Cloud hosting
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 1 }}>
            Run your own application on our managed Kubernetes. Pick the plan that fits. Prices
            are per month, plus GST at {CLOUD_GST_PERCENT}%.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            One unit covers compute: <strong>1 CU = 1 vCPU with 2 GB of memory for one hour</strong>,
            charged on whichever of the two your workload uses up first. Running all month is{" "}
            {CU_HOURS_ALWAYS_ON} CU.
          </Typography>

          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            {CLOUD_PLANS.map((p) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={p.tier}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {p.label}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {formatInr(p.price)}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {" "}
                        /month + GST
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatInrPaise(invoiceTotal(p.price, true))} with GST
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body2">
                      <strong>{p.cu.toLocaleString("en-IN")} CU</strong> compute
                    </Typography>
                    <Typography variant="body2">{formatGb(p.blockStorageGb)} block storage</Typography>
                    <Typography variant="body2">{formatGb(p.objectStorageGb)} object storage</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {formatGb(p.transferOutGb)} data out
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {p.workload}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Support: {p.support}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Bill never above {formatInr(p.maxInvoice)} + GST
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Included on every cloud plan
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                {CLOUD_INCLUDED.map((item) => (
                  <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Going over, and the cap
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: "text.secondary" }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              The first {CLOUD_OVERAGE.gracePercent}% above any allowance is free, once a quarter.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Then compute is added in blocks of {CLOUD_OVERAGE.cuBlockSize} CU at{" "}
              {formatInr(CLOUD_OVERAGE.cuBlock)}, then {formatInr(CLOUD_OVERAGE.cuAfterBlocks)} per
              CU. Block storage {formatInr(CLOUD_OVERAGE.blockStorageGb)}/GB, object storage{" "}
              {formatInr(CLOUD_OVERAGE.objectHotGb)}/GB ({formatInrPaise(CLOUD_OVERAGE.objectArchiveGb)}/GB
              archived), data out {formatInr(CLOUD_OVERAGE.transferOutGb)}/GB. All plus GST.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <strong>Every bill is capped at {CLOUD_BILL_CEILING_MULTIPLE}× the plan price.</strong>{" "}
              Two months over your allowance moves you up one plan, with 14 days&rsquo; notice; you can
              move down at any month end.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              <strong>Your data is never deleted for a commercial reason.</strong> Past a storage cap
              new writes stop; what is stored stays readable and exportable.
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Quoted separately
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: "text.secondary" }}>
            {CLOUD_EXTRAS.map((x) => (
              <Typography component="li" variant="body2" key={x.name} sx={{ mb: 0.5 }}>
                <strong>{x.name}</strong> — {x.price}
              </Typography>
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 3 }}>
            Cloud prices from {CLOUD_SOURCE.templateId} v{CLOUD_SOURCE.templateVersion} (
            {CLOUD_SOURCE.repo}/{CLOUD_SOURCE.file}), approved {CLOUD_SOURCE.approvedOn}, effective{" "}
            {CLOUD_SOURCE.effectiveFrom}. GST at {CLOUD_GST_PERCENT}% is added to every cloud amount.
          </Typography>
          <Button variant="outlined" component={RouterLink} to="/contact" sx={{ mb: 4 }}>
            Talk to us about cloud hosting
          </Button>
        </Box>

        {/* Stated on the price page rather than discovered on the first
            invoice: whether GST is added on top is a real difference to what
            the customer pays, and whether they can claim ITC on it. */}
        {/* 🔑 Derived from ENTITY.gstRegistered, not written out here. The day
            registration lands this sentence becomes FALSE on a live page, and
            it used to be one of three independent copies of the same claim. */}
        {gstNotRegisteredNotice() && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <strong>No GST is charged.</strong> {gstNotRegisteredNotice()}
          </Alert>
        )}
        {gstRegisteredNotice() && (
          <Alert severity="info" sx={{ mb: 4 }}>
            {gstRegisteredNotice()}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 3 }}>
          Prices from {TIER_SOURCE.templateId} v{TIER_SOURCE.templateVersion} (
          {TIER_SOURCE.repo}/{TIER_SOURCE.file}), approved {TIER_SOURCE.approvedOn}, effective{" "}
          {TIER_SOURCE.effectiveFrom}. All amounts in Indian rupees.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button size="large" variant="contained" color="secondary" component={RouterLink} to="/register">
            Register as a Service Provider
          </Button>
          <Button size="large" variant="outlined" component={RouterLink} to="/legal/terms">
            Read the Terms
          </Button>
        </Stack>
      <VersionLine />
      </Container>
    </Box>
  );
}
